import { getBaselineValues } from "../baselines/records";
import { sumTotalEncounterVolumes } from "./encounter-volumes";
import type { OrganizationProfileData, ValidationResult } from "./types";

export function validateProfile(
  profile: OrganizationProfileData,
): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  const totalEncounters =
    sumTotalEncounterVolumes(profile.encounter_volumes) ?? 0;

  if (totalEncounters === 0) {
    errors.push("At least one care setting must have encounters > 0");
  }

  const payerSum = Object.values(profile.payer_mix).reduce((a, b) => a + b, 0);
  if (Math.abs(payerSum - 1.0) > 0.02) {
    errors.push(
      `Payer mix sums to ${(payerSum * 100).toFixed(0)}%, must sum to 100%`,
    );
  }

  if (totalEncounters > 0 && totalEncounters < 10000) {
    warnings.push(
      "Very low encounter volume — pro-forma projections may not be meaningful",
    );
  }

  if (!profile.workforce.coder && !profile.workforce.physician) {
    warnings.push(
      "No workforce data entered — labor savings categories will show $0",
    );
  }

  return { valid: errors.length === 0, errors, warnings };
}

export function validateCategoryApplicability(
  profile: OrganizationProfileData,
  categoryId: string,
): { applicable: boolean; reason?: string } {
  const b = getBaselineValues(profile.baselineRecords);
  switch (categoryId) {
    case "autonomous-coding":
      if (!profile.workforce.coder) {
        return {
          applicable: false,
          reason: "Coder workforce data required for coding ROI",
        };
      }
      break;
    case "prior-authorization":
      if (!b.pa_volume_annual) {
        return {
          applicable: false,
          reason: "PA volume baseline required for PA automation ROI",
        };
      }
      break;
    case "ai-ambient-scribes":
      if (!profile.workforce.physician) {
        return {
          applicable: false,
          reason: "Physician workforce data required for documentation ROI",
        };
      }
      break;
  }

  return { applicable: true };
}
